import { TuiStatus } from '@taiga-ui/kit';
import { pure } from '@core/helpers/pure';
import { TuiChip } from '@taiga-ui/kit';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TextCopierComponent } from '@core/components/text-copier/text-copier.component';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { RequestService } from '@core/services/request/request.service';
import {
  RequestDevice,
  RequestDeviceModelId,
  RequestDeviceModelName,
} from '@core/types/RequestDevice';
import { TuiIcon, TuiLink, TuiButton, TuiHint } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { differenceInDays, format } from 'date-fns';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { DeviceLocationUpdaterComponent } from './components/device-location-updater/device-location-updater.component';
import { RequestDeviceRemoverComponent } from './components/request-device-remover/request-device-remover.component';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { DeviceInstallQualityCheckStatus } from '@core/types/DeviceInstallQuality';
import { Nullable } from '@core/helpers';
import { TamperDtFormattedPipe } from './pipes/tamper-dt-formatted.pipe';
import { DeviceTamperService } from './services/device-tamper.service';

@Component({
  selector: 'x-request-form-devices-device',
  imports: [
    TuiStatus,
    AsyncPipe,
    TuiButton,
    TuiIcon,
    TuiBadge,
    DatePipe,
    TuiHint,
    TuiLink,
    TuiChip,
    DeviceLocationUpdaterComponent,
    RequestDeviceRemoverComponent,
    TextCopierComponent,
    TranslatePipe,
    NgClass,
    TamperDtFormattedPipe,
  ],
  providers: [DeviceTamperService],
  templateUrl: './request-form-devices-device.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormDevicesDeviceComponent implements OnInit, OnDestroy {
  private static readonly FETCH_INSTALL_QUALITY_STATUS = Symbol(
    'FETCH_INSTALL_QUALITY_STATUS',
  );

  private readonly requestService = inject(RequestService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly deviceTamperService = inject(DeviceTamperService);
  private locale = this.outkeepTranslateService.getLocale();
  private installQualityUpdatesSub: Subscription | null = null;
  private readonly installQualitySocketEvent$ = new Subject<void>();

  private readonly refreshInstallQualityStatus$ = new ReplaySubject<
    | DeviceInstallQualityCheckStatus
    | null
    | typeof RequestFormDevicesDeviceComponent.FETCH_INSTALL_QUALITY_STATUS
  >(1);
  readonly permissionService = inject(OutkeepPermissionService);
  readonly $request = this.requestService.$request;
  readonly $device = input.required<RequestDevice>({ alias: 'device' });
  readonly $orderNumber = input.required<number>({ alias: 'orderNumber' });
  readonly $deviceInstallQualityInput = input<
    Nullable<DeviceInstallQualityCheckStatus>
  >(null, {
    alias: 'deviceInstallQualityInput',
  });
  readonly $isInsideSummary = input<boolean>(false, {
    alias: 'isInsideSummary',
  });

  readonly tamperStatus = this.deviceTamperService.tamperStatus;
  readonly tamperLoadState = this.deviceTamperService.tamperLoadState;

  constructor() {
    effect(() => {
      if (this.$isInsideSummary()) {
        this.refreshInstallQualityStatus$.next(
          this.$deviceInstallQualityInput(),
        );
      }
    });
  }

  readonly installQualityStatus$ = this.refreshInstallQualityStatus$.pipe(
    switchMap(installQualityStatusOrRefreshTrigger => {
      if (
        installQualityStatusOrRefreshTrigger ===
        RequestFormDevicesDeviceComponent.FETCH_INSTALL_QUALITY_STATUS
      ) {
        return this.requestDevicesService
          .getDeviceInstallQualityStatus$(this.device.deviceHash)
          .pipe(
            catchError(err => {
              console.error(err);

              return of(null);
            }),
          );
      }

      return of(installQualityStatusOrRefreshTrigger);
    }),
    shareReplay(1),
  );

  private readonly isInstallQualityCheckActive$ =
    this.installQualityStatus$.pipe(
      map(
        installQualityStatus =>
          !!(
            installQualityStatus?.lastRequest &&
            !installQualityStatus.lastRequest.finished
          ),
      ),
      distinctUntilChanged(),
      shareReplay(1),
    );

  ngOnInit(): void {
    this.outkeepTranslateService
      .onLangChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.locale = this.outkeepTranslateService.getLocale();
      });

    if (!this.$isInsideSummary()) {
      this.startInstallQualityUpdatesListener();
    }

    const socketEventSource$ = this.$isInsideSummary()
      ? this.requestDevicesService.listenToInstallQualityUpdates$(
          this.device.deviceHash,
        )
      : this.installQualitySocketEvent$.asObservable();

    this.deviceTamperService.init(
      this.device.deviceHash,
      this.isInstallQualityCheckActive$,
      socketEventSource$,
    );

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.stopInstallQualityUpdatesListener();

          return;
        }

        if (
          event instanceof NavigationEnd &&
          event.urlAfterRedirects.includes('/devices')
        ) {
          this.startInstallQualityUpdatesListener();
        }
      });
  }

  ngOnDestroy(): void {
    this.stopInstallQualityUpdatesListener();
  }

  private startInstallQualityUpdatesListener(): void {
    if (this.$isInsideSummary() || this.installQualityUpdatesSub) {
      return;
    }

    this.refreshInstallQualityStatus$.next(
      RequestFormDevicesDeviceComponent.FETCH_INSTALL_QUALITY_STATUS,
    );

    this.installQualityUpdatesSub = this.requestDevicesService
      .listenToInstallQualityUpdates$(this.device.deviceHash)
      .subscribe(() => {
        this.refreshInstallQualityStatus$.next(
          RequestFormDevicesDeviceComponent.FETCH_INSTALL_QUALITY_STATUS,
        );
        this.installQualitySocketEvent$.next();
      });
  }

  private stopInstallQualityUpdatesListener(): void {
    this.installQualityUpdatesSub?.unsubscribe();
    this.installQualityUpdatesSub = null;
  }

  editRequestDeviceButtonClicked = output<RequestDevice>();
  deviceDataUpdated = output<RequestDevice>();
  requestDeviceRemoved = output<RequestDevice>();

  @pure
  get device() {
    return this.$device();
  }

  @pure
  get deviceName(): string {
    switch (this.device.deviceModelId) {
      case RequestDeviceModelId.INVIS_DUOS:
        return RequestDeviceModelName.INVIS_DUOS;
      case RequestDeviceModelId.INVIS_DUOS_S:
        return RequestDeviceModelName.INVIS_DUOS_S;
      case RequestDeviceModelId.INVIS_DUOS_3D_L:
        return RequestDeviceModelName.INVIS_DUOS_3D_L;
      case RequestDeviceModelId.INVIS_DUOS_ONLINE_LITE:
        return RequestDeviceModelName.INVIS_DUOS_ONLINE_LITE;
      case RequestDeviceModelId.INVIS_DUOS_CONTAINER:
        return RequestDeviceModelName.INVIS_DUOS_CONTAINER;
      default:
        return 'Неизвестная модель';
    }
  }

  @pure
  get lastDeviceDatetime(): Date | null {
    return this.requestDevicesService.getLastDeviceDatetimeOf(this.device);
  }

  @pure
  get lastLocationDatetime(): Date | null {
    return this.requestDevicesService.getLastLocationDatetimeOf(this.device);
  }

  @pure
  get daysFromLastData(): number | null {
    const deviceDt = this.device.status?.device_dt;

    if (!deviceDt) {
      return null;
    }

    const date = new Date(deviceDt);

    return differenceInDays(new Date(), date);
  }

  @pure
  get daysBetweenLocationAndLastDate(): number | null {
    return this.requestDevicesService.getDaysBetweenLocationAndLastDateOf(
      this.device,
    );
  }

  @pure
  get coordinates(): string | null {
    const location = this.device.status?.location;

    if (!location) {
      return null;
    }

    const matches = location.match(
      /POINT\s*\(([-+]?\d*\.?\d+)\s*([-+]?\d*\.?\d+)\)/,
    );

    if (matches) {
      const longitude = parseFloat(matches[1]);
      const latitude = parseFloat(matches[2]);

      if (!longitude || !latitude) {
        return null;
      }

      return `${latitude}, ${longitude}`;
    } else {
      throw new Error('Invalid point format');
    }
  }

  formatDate(input: Date | string, withSeconds = false): string {
    const date = typeof input === 'string' ? new Date(input) : input;

    return format(date, `d MMM yyyy, HH:mm${withSeconds ? ':ss' : ''} XXX`, {
      locale: this.locale,
    });
  }

  scheduleInstallQualityCheck() {
    this.requestDevicesService
      .scheduleDeviceInstallQualityCheck$(
        this.device.request,
        this.device.deviceHash,
      )
      .subscribe();
  }

  @pure
  getCutoffSwitchTranslationKey(cutoffSwitchOption: boolean | null) {
    return `request_form.devices.cutoff_switch_options.${cutoffSwitchOption ? 'yes' : 'no'}`;
  }
}
